import React, { useEffect, useRef, useState } from 'react';
import './BefidCustomerRecovery.css';

function RecoveryCode({ token, expectedLinkId, identityBase, requestHeaders = {}, onReverify, onClose }) {
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const active = useRef(false);
  const pending = useRef(null);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; pending.current?.abort(); pending.current = null; };
  }, []);
  async function generate(event) {
    event.preventDefault();
    if (!consent || pending.current || !active.current) return;
    const controller = new AbortController(); pending.current = controller;
    setBusy(true); setError(null); setCode('');
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
    try {
      const response = await fetch(`${identityBase}/recovery-code`, {
        method: 'POST', credentials: 'include', cache: 'no-store', signal: controller.signal,
        headers: { ...Object.fromEntries(Object.entries(requestHeaders).filter(([name]) => name.toLowerCase() !== 'authorization')), 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ expectedLinkId, confirmation: true }),
      });
      const payload = await response.json().catch(() => null);
      if (!active.current || controller.signal.aborted) return;
      if (!response.ok) {
        setError({ reverify: response.status === 401, message: response.status === 401
          ? 'Vérifiez de nouveau votre téléphone avant de préparer votre code de secours.'
          : response.status === 429 ? 'Patientez avant de demander un nouveau code de secours.'
            : 'Le code n’a pas pu être obtenu. Un ancien code peut avoir été remplacé. Après vérification, générez-en un nouveau et conservez-le.' });
        return;
      }
      if (!/^[a-f0-9]{64}$/i.test(payload?.recoveryCode || '')) throw Error('INVALID_RECOVERY_CODE');
      setCode(payload.recoveryCode); setConsent(false);
    } catch (_) {
      if (active.current && (!controller.signal.aborted || timedOut)) setError({ message: 'La réponse est indisponible. Un ancien code peut avoir été remplacé. Aucun nouvel essai automatique ne sera effectué.' });
    } finally {
      clearTimeout(timeout);
      if (pending.current === controller) pending.current = null;
      if (active.current && (!controller.signal.aborted || timedOut)) setBusy(false);
    }
  }
  return <div className="befid-recovery-code" aria-busy={busy}>
    <h4>Mon code de secours BEFID</h4>
    <p>Préparez ce code tant que vous avez accès à votre téléphone. Si vous perdez votre numéro, il permettra de récupérer la même identité depuis un restaurant déjà lié, après vérification du nouveau numéro par SMS.</p>
    <p>Ce code est personnel et à usage unique. Conservez-le hors de cette application, dans un endroit sûr. Ne le partagez pas avec un restaurant ni avec l’assistance.</p>
    {error && <div role="alert"><p>{error.message}</p>{error.reverify && <button type="button" disabled={busy} onClick={onReverify}>Vérifier de nouveau mon téléphone</button>}</div>}
    {code && <div role="status"><p>Voici votre nouveau code. Il ne pourra plus être affiché après fermeture de cet écran.</p><code className="befid-recovery-code-value">{code}</code><p>Votre précédent code est désormais inutilisable.</p></div>}
    <form onSubmit={generate}>
      <label className="befid-customer-identity-consent"><input type="checkbox" checked={consent} disabled={busy} onChange={event => setConsent(event.target.checked)} /><span>Je comprends que générer un code remplace immédiatement tout précédent code de secours.</span></label>
      <button type="submit" disabled={busy || !consent}>{busy ? 'Préparation…' : code ? 'Remplacer mon code de secours' : 'Générer ou remplacer mon code de secours'}</button>
    </form>
    <button className="befid-identity-secondary" type="button" disabled={busy} onClick={onClose}>{code ? 'J’ai conservé mon code, fermer' : 'Fermer'}</button>
  </div>;
}
export default function BefidRecoveryCode(props) {
  return <RecoveryCode key={`${props.token}\u0000${props.expectedLinkId}\u0000${props.identityBase}\u0000${JSON.stringify(props.requestHeaders || {})}`} {...props} />;
}
